import React from 'react';
import { TFunction } from 'i18next';
import { WithTranslation, withTranslation } from 'react-i18next';

import { DraftOrder, Order } from '../../types/orders';
import { Table, Tr, Th, Thead, Tbody, Td } from '../../components/Table';
import ExpandCollapse from './ExpandCollapse';

interface ExternalId {
    id: string;
    source: string;
    description?: string | undefined;
}

export interface ExternalIdsProps extends WithTranslation {
    draftOrOrder: DraftOrder | Order;
}

const getIds = (draftOrOrder: DraftOrder | Order): ExternalId[] => {
    if ((draftOrOrder as DraftOrder).order) {
        return (draftOrOrder as DraftOrder).order.external_ids || [];
    }
    if (draftOrOrder as Order) {
        return (draftOrOrder as Order).external_ids || [];
    }
    return [];
};

const ExternalIdTable = (
    {
        t,
        externalIds,
        hasDescription,
    }: {
        t: TFunction;
        externalIds: ExternalId[];
        hasDescription: boolean;
    }
) => {
    return (
        <Table>
            <Thead>
                <Tr>
                    <Th>{t('orders.fields.external_ids.source')}</Th>
                    <Th>{t('orders.fields.external_ids.id')}</Th>
                    {hasDescription && <Th>{t('orders.fields.external_ids.description')}</Th>}
                </Tr>
            </Thead>
            <Tbody>
                {externalIds.map((ref, i) => (
                    <Tr key={i}>
                        <Td>{ref.source}</Td>
                        <Td>
                            <code>{ref.id}</code>
                        </Td>
                        {hasDescription && <Td>{ref.description}</Td>}
                    </Tr>
                ))}
            </Tbody>
        </Table>
    );
};

const ExternalIds = (
    {
        t,
        draftOrOrder,
    }: ExternalIdsProps
) => {
    const externalIds = getIds(draftOrOrder);

    if (externalIds.length === 0) {
        return null;
    }
    const hasDescription = externalIds.reduce((acc, elem) => acc || elem.description !== undefined, false);

    return (
        <>
            <ExpandCollapse
                label={t('orders.fields.external_ids.click_to_show_external_ids')}
                content={<ExternalIdTable t={t} hasDescription={hasDescription} externalIds={externalIds} />}
            />
        </>
    );
};

export default withTranslation()(ExternalIds);
